import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Box, Card, styled } from "@mui/material";
import Button, { type ButtonProps } from "@mui/material/Button";
import styles from "./login.module.scss";
import commonStyles from "../../App.module.scss";
import { object, string, ref, ValidationError } from "yup";
import { apiRequest, catchErrorMessageHandler } from "../../infrastructure/backendService";
import {
  CustomInputLabel,
  CustomLeftIcon,
  CustomRequiredInputLabel,
} from "../common/StyledComponents/StyledFields";
import toast, { Toaster } from "react-hot-toast";
// import ToastSuccess from "../common/toast/toastSuccess";
// import ToastError from "../common/toast/toastError";
import { isNil } from "lodash";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import apiPath from "../../config/apiPaths.json";
// import ToastAttention from "../common/toast/toastAttention";
import LOCALE from '../../config/en-us.json';
import constants from "../../config/constants.json";
import SuccessPopup from "../devotee/donation/successPopup";
import ErrorPopup from "../common/ErrorPopup";
import infoIcon from "../../assets/svg/info_icon.svg";


const CustomTextField = styled(TextField)`
  & input {
    padding: 8px;
    height: 25px;
    border-radius: 8px;
    background: #fff9e4;
  }
  & fieldset {
    border-style: none;
  }
`;

interface UserForm {
  username: string;
  secretQuestion: string;
  secretAnswer: string;
  verificationCode: string;
  newpassword: string;
  confirmpassword: string;
}

interface userNameErrorType {
  username: string;
}

interface secretFieldErrorsType {
  secretQuestion: string;
  secretAnswer: string;
}

interface validationFieldErrorsType {
  username: string;
  verificationCode: string;
  newpassword: string;
  confirmpassword: string;
}

interface newPasswordFieldErrorsType {
  newpassword: string;
  confirmpassword: string;
}

interface forgotPasswordProps {
  setShowLoginForm: React.Dispatch<React.SetStateAction<boolean>>;
  setShowForgotPassword: React.Dispatch<React.SetStateAction<boolean>>;
  setShowError: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ForgotPassword(props: forgotPasswordProps) {
  const [showPasswordInfo, setShow] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [userForm, setUserForm] = useState<UserForm>({
    username: "",
    secretQuestion: "",
    secretAnswer: "",
    verificationCode: "",
    newpassword: "",
    confirmpassword: "",
  });

  const [errorMessage, setErrorMessage] = useState({
    username: "",
    secretQuestion: "",
    secretAnswer: "",
    verificationCode: "",
    newpassword: "",
    confirmpassword: "",
  });

  const [showSecretFields, setShowSecretFields] = useState<boolean>(false);
  const [showVerificationField, setShowVerificationField] =
    useState<boolean>(false);
  const [showNewpasswordField, setShowNewpasswordField] =
    useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
  const [userNameErrorMessage, setUserNameErrorMessage] =
    useState<userNameErrorType>({
      username: "",
    });

  const [secretFieldErrors, setSecretFieldsError] =
    useState<secretFieldErrorsType>({
      secretAnswer: "",
      secretQuestion: "",
    });

  const [validationFieldErrors, setValidationFieldErrors] =
    useState<validationFieldErrorsType>({
      username: "",
      verificationCode: "",
      newpassword: "",
      confirmpassword: "",
    });

  const [newPassswordFieldErrors, setNewPassswordFieldErrors] =
    useState<newPasswordFieldErrorsType>({
      newpassword: "",
      confirmpassword: "",
    });

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () =>
    setShowConfirmedPassword((show) => !show);
  const handleMouseDownReenterPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const validationSchemaForUserName = object().shape({
    username: string().required("Username is required"),
  });

  const validationSchemaForSecretFields = object().shape({
    secretAnswer: string().required("Secret Answer is required"),
    secretQuestion: string().required("Secret Question is required"),
  });

  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@`#^_$!%*?&])[A-Za-z\d@`#^_$!%*?&]{9,16}$/;
  const validationSchemaForVerificationFields = object().shape({
    username: string().required("Username is required"),
    verificationCode: string().required("Verification Code is required"),
    newpassword: string()
    .min(9, "Password must be at least 9 characters")
    .max(16, "Password must be no more than 16 characters")
    .required("Password is required")
    .test(
      'password-complexity',
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
      (value) => {
        const uppercaseRegex = /[A-Z]/;
        const lowercaseRegex = /[a-z]/;
        const numberRegex = /[0-9]/;
        const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

        let errors = [];

        if (!uppercaseRegex.test(value)) {
          errors.push('uppercase letter');
        }
        if (!lowercaseRegex.test(value)) {
          errors.push('lowercase letter');
        }
        if (!numberRegex.test(value)) {
          errors.push('number');
        }
        if (!specialCharRegex.test(value)) {
          errors.push('special character');
        }

        if (errors.length > 0) {
          return new ValidationError(
            `Password must contain at least one ${errors.join(', ')}.`,
            null,
            'newpassword'
          );
        }

        return true;
      }
    ),
      confirmpassword: string()
      .required("Confirm Password is required")
      .oneOf([ref('newpassword'), ''], 'New Password and Confirm Password must match'),
  });

  const newPasswordSchemaForCreatePasswordFields = object().shape({
    newpassword: string().required("New Password is required"),
    confirmpassword: string()
      .required("Confirm Password is required")
      .oneOf(
        [ref("newpassword"), ""],
        "New Password and Confirm Password must match"
      ),
  });

  const handleFieldChange = (field: any, value: any): void => {
    setUserForm({
      ...userForm,
      [field]: value,
    });
  };

  const onClickNext = async (): Promise<void> => {
    const errors: userNameErrorType = {
      username: "",
    };

    try {
      // Validate the form data against the validation schema
      await validationSchemaForUserName.validate(userForm, {
        abortEarly: false,
      });

      // If validation passes, you can submit the form
      setUserNameErrorMessage(errors);
      // setShowSecretFields(true);
      // setShowVerificationField(false);
      // setShowNewpasswordField(false);

      // Call your API or perform other actions here
      verifyUsernameApiCall();
    } catch (error) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            errors[e.path as keyof userNameErrorType] = e.message;
          }
        });
      }
      setUserNameErrorMessage(errors);
    }
  };

  const verifyUsernameApiCall = () => {
    apiRequest({
      method: "GET",
      endpoint: `${apiPath.usernameCheck}/${userForm.username}`,
    })
      .then((res) => {
        const userFromApi = res.data;
        console.log(userFromApi, "getttttttttttt");
        const successMessage = "verified username successfully!";
        if (res.data.usernameExists === false) {
          toast.custom(<ErrorPopup showMessage={res.data.Message} isOpen={isOpen} setIsOpen={setIsOpen} />, {
            duration: constants.TOAST_POPUP_DURATION,
          });
        }
        // setUserForm({
        //    ...userForm,
        //    username: userFromApi.username,
        // });
        // setUserForm({
        //    ...userForm,
        //    secretQuestion: userFromApi.hintQuestion,
        // });
        setShowSecretFields(res.data.usernameExists);
        setShowVerificationField(false);
        setShowNewpasswordField(false);
      })
      .catch((err) => {
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        userNameField();
        setShowSecretFields(false);
        setShowVerificationField(false);
        setShowNewpasswordField(false);
      });
  };

  const secretFieldOnClick = async (): Promise<void> => {
    const errors: secretFieldErrorsType = {
      secretAnswer: "",
      secretQuestion: "",
    };

    try {
      // Validate the form data against the validation schema
      await validationSchemaForSecretFields.validate(userForm, {
        abortEarly: false,
      });

      // If validation passes, you can submit the form
      setSecretFieldsError(errors);
      console.log("secret questions........");

      //remove below lines when Api integrated
      // Call your API or perform other actions here
      verifySecretFieldsApiCall();
    } catch (error) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            errors[e.path as keyof secretFieldErrorsType] = e.message;
          }
        });
      }
      setSecretFieldsError(errors);
    }
  };

   const verifySecretFieldsApiCall = () => {
      const reqBody = {
         hintQuestion: userForm.secretQuestion,
         hintAnswer: userForm.secretAnswer,
         username: userForm.username
      };
      apiRequest({
         method: 'POST',
         endpoint: apiPath.validateHintQuestionAnswerApi,
         body: JSON.stringify(reqBody),
      })
      .catch((err) => {
        console.log(err);
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setShowSecretFields(true);
        setShowVerificationField(false);
        setShowNewpasswordField(false);
      });
  };

  const onClickSubmitCode = async (): Promise<void> => {
    const errors: validationFieldErrorsType = {
      username: "",
      verificationCode: "",
      newpassword: "",
      confirmpassword: "",
    };

    try {
      // Validate the form data against the validation schema
      await validationSchemaForVerificationFields.validate(userForm, {
        abortEarly: false,
      });
      // If validation passes, you can submit the form
      setValidationFieldErrors(errors);

      // Call your API or perform other actions here
      updateNewPasswordByMailSubmitApiCall();
    } catch (error) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            errors[e.path as keyof validationFieldErrorsType] = e.message;
          }
        });
      }
      setValidationFieldErrors(errors);
    }
  };

  const updateNewPasswordByMailSubmitApiCall = () => {
    const reqBody = {
      newPassword: userForm.newpassword,
      confirmNewPassword: userForm.confirmpassword,
      verificationCode: userForm.verificationCode,
    };
    apiRequest({
      method: "POST",
      endpoint: `${apiPath.resetPasswordByMail}/${userForm.username}/code`,
      body: JSON.stringify(reqBody),
    })
      .then((res) => {
        console.log(res, "password created successfully");
        const successMessage =LOCALE.PASSWORD_CREATED;
        toast.custom(<SuccessPopup headerMessage="Password Update" showMessage={successMessage} isOpen={isOpen} setIsOpen={setIsOpen} />);
        setUserForm({
          username: "",
          secretQuestion: "",
          secretAnswer: "",
          verificationCode: "",
          newpassword: "",
          confirmpassword: "",
        });
        props.setOpen(false);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setShowSecretFields(false);
        setShowVerificationField(true);
        setShowNewpasswordField(false);
      });
  };

  const verificationCodeApiCall = () => {
    const reqBody = {
      username: userForm.username,
      verificationCode: userForm.verificationCode,
      hintQuestion: userForm.secretQuestion,
      hintAnswer: userForm.secretAnswer,
    };

    apiRequest({
      method: "POST",
      endpoint: apiPath.updateNewPasswordByQuestionApi,
      body: JSON.stringify(reqBody),
    })
      .then((res) => {
        console.log(res);
        toast.custom(<SuccessPopup headerMessage="Password Update" showMessage={LOCALE.EMAIL_ALERT} isOpen={isOpen} setIsOpen={setIsOpen} />,
        {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setUserForm({
          username: "",
          secretQuestion: "",
          secretAnswer: "",
          verificationCode: "",
          newpassword: "",
          confirmpassword: "",
        });
        setShowSecretFields(false);
        setShowVerificationField(false);
        setShowNewpasswordField(true);
      })
      .catch((err) => {
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        props.setShowError(true);
        props.setShowLoginForm(false);
        props.setShowForgotPassword(false);
      });
  };

  const onClickSendVerificationMail = async (): Promise<void> => {
    const errors: validationFieldErrorsType = {
      username: "",
      verificationCode: "",
      newpassword: "",
      confirmpassword: "",
    };

    verificationCodeBYMailApiCall();
  };

  const verificationCodeBYMailApiCall = () => {
    const reqBody = {
      username: userForm.username,
      // verificationCode: userForm.verificationCode,
    };

    apiRequest({
      method: "GET",
      endpoint: `${apiPath.resetPasswordByMail}/${userForm.username}/code`,
      // body: JSON.stringify(reqBody),
    })
      .then((res) => {
        const successMessage = LOCALE.VERIFICATION_ALERT;
        // toast.custom(<ToastSuccess message={successMessage} sent={""} />, {
        //   duration: 10000,
        // });
        toast.custom(<SuccessPopup headerMessage="Verification" showMessage={successMessage} isOpen={isOpen} setIsOpen={setIsOpen} />,
        {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setUserForm({
          username: "",
          secretQuestion: "",
          secretAnswer: "",
          verificationCode: "",
          newpassword: "",
          confirmpassword: "",
        });
        setShowSecretFields(false);
        setShowVerificationField(true);
        setShowNewpasswordField(false);
      })
      .catch((err) => {
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setShowSecretFields(true);
        setShowVerificationField(false);
        setShowNewpasswordField(false);
      });
  };

  const onNewpasswordSubmit = async (): Promise<void> => {
    const errors: newPasswordFieldErrorsType = {
      newpassword: "",
      confirmpassword: "",
    };

    try {
      // Validate the form data against the validation schema
      await newPasswordSchemaForCreatePasswordFields.validate(userForm, {
        abortEarly: false,
      });
      // If validation passes, you can submit the form
      setNewPassswordFieldErrors(errors);

      // Call your API or perform other actions here
      newPasswordSubmitApiCall();
    } catch (error) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e) => {
          if (e.path) {
            errors[e.path as keyof newPasswordFieldErrorsType] = e.message;
          }
        });
      }
      setNewPassswordFieldErrors(errors);
    }
  };

  const newPasswordSubmitApiCall = () => {
    const reqBody = {
      hintQuestion: userForm.secretQuestion,
      hintAnswer: userForm.secretAnswer,
      username: userForm.username,
      newPassword: userForm.newpassword,
      confirmNewPassword: userForm.confirmpassword,
      // verificationCode: userForm.verificationCode,
    };

    apiRequest({
      method: "POST",
      endpoint: apiPath.updateNewPasswordByQuestionApi,
      body: JSON.stringify(reqBody),
    })
      .then((res) => {
        const successMessage = LOCALE.PASSWORD_CREATED;
        // toast.custom(<ToastSuccess message={successMessage} sent={""} />, {
        //   duration: 10000,
        // });
        toast.custom(<SuccessPopup headerMessage="Password" showMessage={successMessage} isOpen={isOpen} setIsOpen={setIsOpen} />,
        {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setUserForm({
          username: "",
          secretQuestion: "",
          secretAnswer: "",
          verificationCode: "",
          newpassword: "",
          confirmpassword: "",
        });
        props.setOpen(false);
      })
      .catch((err) => {
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setShowNewpasswordField(true);
        setShowSecretFields(false);
        setShowVerificationField(false);
        setShowNewpasswordField(false);
      });
  };

  const onClickCancel = (): void => {
    // props.setShowLoginForm(true);
    // props.setShowForgotPassword(false);
    setUserForm({
      username: "",
      secretQuestion: "",
      secretAnswer: "",
      verificationCode: "",
      newpassword: "",
      confirmpassword: "",
    });
  };

  const handleNextPress = (e:any) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent the default form submission behavior
      onClickNext();
    }
  };

  const userNameField = (): React.ReactElement => {
    return (
      <form style={{ marginTop: "20px" }} className={styles.Form} onKeyDown={handleNextPress}>
        <Grid xs={12} item>
          {CustomRequiredInputLabel("User Name", true)}
          <CustomTextField
            fullWidth={true}
            value={userForm.username}
            // onChange={(e) => {
            //    setUserNameErrorMessage({ ...userNameErrorMessage, username: '' });
            //    handleFieldChange('username', e.target.value);
            // }}
            onChange={(e) => {
              let inputUserName = e.target.value;
              inputUserName = inputUserName.toUpperCase();
              if (inputUserName.length <= 10 && !/[\s!@#$%^&*()]/.test(inputUserName)) {
                // Check length and absence of spaces
                setUserNameErrorMessage({
                  ...userNameErrorMessage,
                  username: "",
                });
                handleFieldChange("username", inputUserName);
              } else {
                setErrorMessage({
                  ...errorMessage,
                  username: "",
                });
              }
            }}
            className={
              userNameErrorMessage.username
                ? styles.errorTextField
                : styles.textField
            }
          />
          {userNameErrorMessage.username && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {userNameErrorMessage.username}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item sx={{ marginTop: "20px" }}>
          <Button
            color="warning"
            fullWidth={true}
            variant="contained"
            onClick={onClickNext}
          >
            Next
          </Button>
          <Button
            color="warning"
            fullWidth={true}
            variant="outlined"
            sx={{ marginTop: "20px" }}
            onClick={onClickCancel}
          >
            Reset
          </Button>
        </Grid>
      </form>
    );
  };

  const secretFields = (): React.ReactElement => {
    return (
      <Box sx={{ marginTop: "20px" }} className={styles.Form}>
        {/* <Grid xs={12} item>
               {CustomRequiredInputLabel("Secret Question", true)}
               <CustomTextField
                  aria-readonly

                  fullWidth={true}
                  helperText={errorMessage.secretQuestion}
                  value={userForm.secretQuestion}
                  onChange={(e) => {
                     setSecretFieldsError({ ...secretFieldErrors, secretQuestion: '' });
                     handleFieldChange('secretQuestion', e.target.value);
                  }}
                  className={secretFieldErrors.secretQuestion ? styles.errorTextField : styles.textField}
               />
               {secretFieldErrors.secretQuestion && (
                  <Typography sx={{ color: 'red', fontSize: '12px' }}>
                     {secretFieldErrors.secretQuestion}
                  </Typography>
               )}
            </Grid>
            <Grid xs={12} item sx={{ marginTop: '20px' }}>
               {CustomRequiredInputLabel("Answer to Secret", true)}
               <CustomTextField

                  fullWidth={true}
                  helperText={errorMessage.secretAnswer}
                  value={userForm.secretAnswer}
                  onChange={(e) => {
                     setSecretFieldsError({ ...secretFieldErrors, secretAnswer: '' });
                     handleFieldChange('secretAnswer', e.target.value);
                  }}
                  className={secretFieldErrors.secretAnswer ? styles.errorTextField : styles.textField}
                  type="password"
               />
               {secretFieldErrors.secretAnswer && (
                  <Typography sx={{ color: 'red', fontSize: '12px' }}>
                     {secretFieldErrors.secretAnswer}
                  </Typography>
               )}
            </Grid> */}
        <Grid xs={12} item sx={{ marginTop: "20px" }}>
          {/* <Button
                  color="warning"
                  fullWidth={true}
                  variant="contained"
                  onClick={secretFieldOnClick}
               >
                  Submit
               </Button> */}
          <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
            <Typography>
              {/* <b style={{display: "flex", justifyContent: "center"}}>Or</b> <br/>  */}
              you can request an identity verification code via your e-mail
              (that is valid for 3 hours) to reset password by clicking the
              following button.
            </Typography>
          </Box>
          <Button
            color="warning"
            fullWidth={true}
            variant="outlined"
            onClick={onClickSendVerificationMail}
          >
            Send Verification Email
          </Button>
        </Grid>
      </Box>
    );
  };

  const handleSubmitPress = (e:any) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent the default form submission behavior
      onClickSubmitCode();
    }
  };

  const verificationFieldCancel = (): void => {
    // setShowSecretFields(true);
    // setShowVerificationField(false);
    console.log("cancelllllllllll")
    setUserForm({
    username: "",
    secretQuestion: "",
    secretAnswer: "",
    verificationCode: "",
    newpassword: "",
    confirmpassword: "",
    })
  };

  const verificationField = (): React.ReactElement => {
    return (
      <form  className={styles.Form} onKeyDown={handleSubmitPress}>
        <Grid xs={12} item>
          {CustomRequiredInputLabel("User Name", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.username}
            value={userForm.username}
            // onChange={(e) => {
            //    setValidationFieldErrors({ ...validationFieldErrors, username: '' });
            //    handleFieldChange('username', e.target.value);
            // }}
            onChange={(e) => {
              const inputUserName = e.target.value;
              if (inputUserName.length <= 10 && !/[\s!@#$%^&*()]/.test(inputUserName)) {
                // Check length and absence of spaces
                setValidationFieldErrors({
                  ...validationFieldErrors,
                  username: "",
                });
                handleFieldChange("username", inputUserName);
              }
            }}
            className={
              validationFieldErrors.username
                ? styles.errorTextField
                : styles.textField
            }
          />
          {validationFieldErrors.username && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {validationFieldErrors.username}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item >
          {CustomRequiredInputLabel("Verification Code", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.verificationCode}
            value={userForm.verificationCode}
            onChange={(e) => {
              setValidationFieldErrors({
                ...validationFieldErrors,
                verificationCode: "",
              });
              handleFieldChange("verificationCode", e.target.value);
            }}
            inputProps={{ maxLength: 6 }}
            error={Boolean(errorMessage.verificationCode)}
            className={
              validationFieldErrors.verificationCode
                ? styles.errorTextField
                : styles.textField
            }
          />
          {validationFieldErrors.verificationCode && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {validationFieldErrors.verificationCode}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item >
          {CustomRequiredInputLabel("New Password", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.newpassword}
            value={userForm.newpassword}
            onChange={(e) => {
              setValidationFieldErrors({
                ...validationFieldErrors,
                newpassword: "",
              });
              handleFieldChange("newpassword", e.target.value);
            }}
            className={
              validationFieldErrors.newpassword
                ? styles.errorTextField
                : styles.textField
            }
          />
          {validationFieldErrors.newpassword && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {validationFieldErrors.newpassword}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item >
          {CustomRequiredInputLabel("Confirm New Password", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.confirmpassword}
            value={userForm.confirmpassword}
            onChange={(e) => {
              setValidationFieldErrors({
                ...validationFieldErrors,
                confirmpassword: "",
              });
              handleFieldChange("confirmpassword", e.target.value);
            }}
            className={
              validationFieldErrors.confirmpassword
                ? styles.errorTextField
                : styles.textField
            }
          />
          {validationFieldErrors.confirmpassword && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {validationFieldErrors.confirmpassword}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item sx={{ marginTop: "10px" }}>
          <Button
            color="warning"
            fullWidth={true}
            variant="contained"
            onClick={onClickSubmitCode}
          >
            Submit
          </Button>
          <Button
            color="warning"
            fullWidth={true}
            variant="outlined"
            sx={{ marginTop: "10px" }}
            onClick={verificationFieldCancel}
          >
            Reset
          </Button>
        </Grid>
        <Grid sm={12} xs={12} item>
          <Card className={commonStyles.passwordHints}>
              <ul>
                  <li>{LOCALE.PASSWORD_HINT_ONE }</li>
                  <li>{LOCALE.PASSWORD_HINT_TWO}</li>
              </ul>
            </Card>
        </Grid>
      </form>
    );
  };

  const newPasswordField = (): React.ReactElement => {
    return (
      <form style={{ marginTop: "20px" }} className={styles.Form} onSubmit={onNewpasswordSubmit}>
        <Grid xs={12} item>
          {CustomRequiredInputLabel("New Password", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.newpassword}
            value={userForm.newpassword}
            onChange={(e) => {
              setNewPassswordFieldErrors({
                ...newPassswordFieldErrors,
                newpassword: "",
              });
              handleFieldChange("newpassword", e.target.value);
            }}
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className={
              newPassswordFieldErrors.newpassword
                ? styles.errorTextField
                : styles.textField
            }
          />
          {newPassswordFieldErrors.newpassword && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {newPassswordFieldErrors.newpassword}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item sx={{ marginTop: "20px" }}>
          {CustomRequiredInputLabel("Confirm New Password", true)}
          <CustomTextField
            fullWidth={true}
            helperText={errorMessage.confirmpassword}
            value={userForm.confirmpassword}
            onChange={(e) => {
              setNewPassswordFieldErrors({
                ...newPassswordFieldErrors,
                confirmpassword: "",
              });
              handleFieldChange("confirmpassword", e.target.value);
            }}
            type={showConfirmedPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownReenterPassword}
                    edge="end"
                  >
                    {showConfirmedPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className={
              newPassswordFieldErrors.confirmpassword
                ? styles.errorTextField
                : styles.textField
            }
          />
          {newPassswordFieldErrors.confirmpassword && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {newPassswordFieldErrors.confirmpassword}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} item sx={{ marginTop: "20px" }}>
          <Button
          type="submit"
            color="warning"
            fullWidth={true}
            variant="contained"
            onClick={onNewpasswordSubmit}
          >
            Submit
          </Button>
          <Button
            color="warning"
            fullWidth={true}
            variant="outlined"
            sx={{ marginTop: "20px" }}
            // onClick={verificationFieldCancel}
          >
            Cancel
          </Button>
        </Grid>
      </form>
    );
  };

  const navigateBack = () => {
    props.setShowLoginForm(true);
    props.setShowForgotPassword(false);
  };

  const userFields = (): React.ReactElement => {
    return (
      <Box>
        <Box sx={{ marginTop: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <div onClick={navigateBack} style={{ marginRight: "10px" }}>
              <CustomLeftIcon />
            </div>
            <Typography align="left" sx={{ fontSize: "24px" }}>
              Forgot Password
            </Typography>
            
          </Box>
        </Box>
        {!showSecretFields && !showVerificationField && !showNewpasswordField
          ? userNameField()
          : ""}
        {showSecretFields && !showVerificationField ? secretFields() : ""}
        {showVerificationField && !showSecretFields && !showNewpasswordField
          ? verificationField()
          : ""}
        {showNewpasswordField ? newPasswordField() : ""}
      </Box>
    );
  };

  return <div className="forgotPasswordSection">{userFields()}</div>;
}
